// Masked trip comms on the staff trip / reservation detail: which trip line the trip has, whether its masked session is
// open, and the full call and text log between driver and client (read-only for staff). Dispatch (edit access) can press
// "Call client": their own saved mobile rings first from the TRIP LINE, they press 1, and the client is called from the
// trip line. Real numbers are never shown here — only the last four digits, so dispatch can tell people apart.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button3D from '../ui/Button3D';
import { formatPhone } from '../../lib/phone';

const SERVICE_LABEL = { AX: 'AX', P2P: 'P2P', CH: 'CH' };
const REASON = {
    no_free_line: 'No trip line was free when the driver accepted. The driver was told to contact dispatch. This is retried automatically when a line frees up.',
    no_driver_mobile: 'The driver has no mobile number on file.',
    no_client_phone: 'The client has no phone number on file.',
    masking_off: 'Masked calling and texting is turned off for this company.',
};
const CLOSE_REASON = {
    trip_finished: 'trip finished', trip_canceled: 'trip canceled', trip_no_show: 'no-show', max_age: 'safety time limit reached',
    driver_reassigned: 'driver reassigned', driver_released: 'driver released', manual: 'closed by staff',
};
const EVENT_STATUS = {
    sent: 'delivered to Twilio', failed: 'not sent', held_test: 'held (Test mode)', blocked_opt_out: 'not sent (opted out)',
    rate_limited: 'not sent (too many)', completed: 'connected', no_answer: 'no answer', busy: 'busy', canceled: 'canceled',
    first_no_answer: 'first phone did not answer', answered: 'connected', requested: 'starting', first_ringing: 'ringing',
    first_answered: 'answered, waiting for 1', connecting: 'connecting', callee_ringing: 'ringing', received: 'received', logged: '',
    opted_out: 'opted out of texts', opted_in: 'texts back on',
};

function newKey() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `stf-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 12)}`;
}

function eventLine(event) {
    const who = event.kind === 'notice'
        ? ({ client_to_driver: 'Client', driver_to_client: 'Driver', system: 'System' }[event.direction] || '')
        : ({ client_to_driver: 'Client → driver', driver_to_client: event.origin === 'staff' ? 'Dispatch → client' : 'Driver → client', system: 'System' }[event.direction] || '');
    const what = event.kind === 'call' ? 'Call' : event.kind === 'sms' ? 'Text' : 'Notice';
    const status = EVENT_STATUS[event.status] ?? event.status;
    const duration = event.kind === 'call' && event.duration ? ` · ${event.duration}s` : '';
    return { who, what, status: status ? `${status}${duration}` : duration.replace(/^ · /, '') };
}

/** The client's latest STOP/CANCEL/START on the current session: true while they are opted out of texts on this line. */
export function clientOptedOut(session) {
    const words = (session?.events || []).filter((event) => event.kind === 'notice' && event.direction === 'client_to_driver'
        && (event.status === 'opted_out' || event.status === 'opted_in'));
    return words.length > 0 && words[words.length - 1].status === 'opted_out';
}

function stamp(value) {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function TripMaskedLinePanel({ engagementId, client, canCall = false, theme = 'light' }) {
    const [view, setView] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState('');
    const key = useRef(newKey());

    const load = useCallback(async () => {
        if (!engagementId || !client?.rpc) return;
        const { data, error: loadError } = await client.rpc('trip_comms_staff_view', { p_engagement_id: engagementId });
        if (loadError) { setError('Could not load this trip’s masked calls and texts.'); return; }
        setError('');
        setView(data);
    }, [client, engagementId]);

    useEffect(() => { load(); }, [load]);

    async function callClient() {
        setBusy(true); setNotice(''); setError('');
        const { data, error: callError } = await client.functions.invoke('trip-line-actions', {
            body: { action: 'call', engagementId, idempotencyKey: key.current },
        });
        setBusy(false);
        key.current = newKey();
        if (callError || data?.ok === false) {
            let message = data?.error;
            try { message = message || (await callError?.context?.json?.())?.error; } catch { /* keep fallback */ }
            setError(message || 'The call could not be started. Nothing was dialed.');
            return;
        }
        setNotice(data?.message || 'Your phone is ringing from the trip line. Answer and press 1 to call the client.');
        load();
    }

    if (!engagementId || !client?.rpc) return null;
    const sessions = view?.sessions || [];
    if (view && sessions.length === 0) return null; // no pool configured, or no driver accepted yet
    const current = sessions[0];
    const cardCls = theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';

    return (
        <section className={`rounded-xl border p-5 space-y-3 ${cardCls}`} aria-label="Masked trip line" data-testid="trip-masked-line">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-bold">
                    Masked trip line{view?.service_code ? ` · ${SERVICE_LABEL[view.service_code] || view.service_code}` : ''}{view?.confirmation_number ? ` · ${view.confirmation_number}` : ''}
                </h3>
                {current?.status === 'open' && canCall && (
                    <Button3D variant="go" onClick={callClient} disabled={busy} data-testid="trip-masked-call">
                        {busy ? 'Ringing your phone…' : 'Call client'}
                    </Button3D>
                )}
            </div>
            {!view && !error && <p className="text-sm opacity-70" aria-busy="true">Loading…</p>}
            {error && <p role="alert" className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm">{error}</p>}
            {current && (
                <dl className="grid grid-cols-1 gap-2 text-sm md:grid-cols-3">
                    <div>
                        <dt className="text-xs uppercase tracking-wide opacity-70">Trip line</dt>
                        <dd className="font-semibold">{current.line_phone ? `${formatPhone(current.line_phone) || current.line_phone}${current.line_label ? ` · ${current.line_label}` : ''}` : 'None'}</dd>
                    </div>
                    <div>
                        <dt className="text-xs uppercase tracking-wide opacity-70">Session</dt>
                        <dd className="font-semibold" data-testid="trip-masked-status">
                            {current.status === 'open' && (current.closes_at ? `Open · closes ${stamp(current.closes_at)}` : 'Open')}
                            {current.status === 'unavailable' && 'Unavailable'}
                            {current.status === 'closed' && `Closed${current.close_reason ? ` · ${CLOSE_REASON[current.close_reason] || current.close_reason}` : ''}`}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-xs uppercase tracking-wide opacity-70">Numbers (last 4)</dt>
                        <dd className="font-semibold">
                            {current.client_phone_last4 ? `Client ••${current.client_phone_last4} · Driver ••${current.driver_phone_last4}` : '—'}
                        </dd>
                    </div>
                </dl>
            )}
            {current && clientOptedOut(current) && (
                <p role="alert" data-testid="trip-masked-opted-out" className="rounded-lg border border-amber-500/60 bg-amber-500/10 p-3 text-sm">
                    The client texted an opt-out word (such as STOP or CANCEL) to the trip line. Texts to them have stopped. The trip was NOT canceled — call the client to confirm.
                </p>
            )}
            {current?.status === 'unavailable' && (
                <p role="status" className="rounded-lg border border-amber-500/60 bg-amber-500/10 p-3 text-sm">{REASON[current.unavailable_reason] || 'Masked line unavailable.'}</p>
            )}
            {sessions.map((session) => (
                <div key={session.id} className="space-y-1">
                    {sessions.length > 1 && (
                        <p className="text-xs font-bold opacity-70">Session opened {stamp(session.opened_at)} · {session.status}</p>
                    )}
                    {(session.events || []).length === 0 && <p className="text-xs opacity-70">No calls or texts yet.</p>}
                    <ol className="space-y-1" aria-label="Calls and texts">
                        {(session.events || []).map((event) => {
                            const line = eventLine(event);
                            return (
                                <li key={event.id} className="rounded-lg border border-[var(--border,#cbd5e1)] px-3 py-2 text-sm" data-testid="trip-masked-event">
                                    <span className="font-semibold">{line.what}</span> · {line.who} · <span className="opacity-80">{stamp(event.at)}</span>
                                    {line.status ? <span className="opacity-80"> · {line.status}</span> : null}
                                    {event.body && event.kind === 'sms' ? <p className="mt-1 whitespace-pre-wrap">{event.body}</p> : null}
                                    {event.detail ? <p className="mt-1 text-xs opacity-80">{event.detail}</p> : null}
                                </li>
                            );
                        })}
                    </ol>
                </div>
            ))}
            {notice && <p role="status" className="text-sm text-emerald-700 dark:text-emerald-300">{notice}</p>}
        </section>
    );
}
